import React, { useState } from 'react'
import {
  FetchUserScreeningsScreeningTypeEnum,
  FetchUserScreeningsStatusEnum,
  OrderByDirection,
  PaginationResponse,
  UserScreeningRecord,
  UserScreeningRecordScreeningTypeEnum,
  UserScreeningStatuses,
} from '../../swagger'
import UserScreeningTable from './UserScreeningTable'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { DEFAULT_PAGE_SIZE } from '../../utils/constants'
import useEndpoint from '../../hooks/useEndpoint'
import { screening } from '../../api/swagger'
import { useMountEffect } from '../../hooks/useMountEffect'
import CardFormHeader from '../Card/CardFormHeader'
import { Box, Card, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Header, { HeaderVariant } from '../Elements/Header'
import UserScreeningTableSearchAndFilters from './UserScreeningTableSearchAndFilters'
import ConfirmUserScreeningStatusUpdateModal from '../Modals/ConfirmUserScreeningStatusUpdateModal'

export interface RowActionDataUpdate {
  status: UserScreeningStatuses
  userScreeningKey: number
  screeningType: UserScreeningRecordScreeningTypeEnum
}

const UserScreeningTab: React.FC = (): React.JSX.Element => {
  const {
    Legal: { fetchUserScreenings: fetchUserScreeningsLoadingId },
  } = useLoadingIds()

  const { t } = useTranslation()
  const theme = useTheme()

  const [userScreenings, setUserScreenings] = useState<UserScreeningRecord[]>(
    []
  )

  const [filtersValue, setFiltersValue] = useState<{
    searchValue: string
    screeningType: FetchUserScreeningsScreeningTypeEnum | undefined
    screeningStatus: FetchUserScreeningsStatusEnum | undefined
  }>({
    searchValue: '',
    screeningType: undefined,
    screeningStatus: undefined,
  })
  const [rowActionDataUpdate, setRowActionDataUpdate] =
    useState<RowActionDataUpdate>()

  const [showModal, setShowModal] = useState(false)

  const [userScreeningsPagination, setUserScreeningsPagination] =
    useState<PaginationResponse>({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalCount: 0,
      orderBy: [{ userEmail: OrderByDirection.Desc }],
    })

  const { triggerFetch: triggerFetchUserScreenings } = useEndpoint({
    loadingId: fetchUserScreeningsLoadingId,
    swaggerCall: async () => {
      const orderByFormatted: Array<string> = ['userEmail DESC']
      if (userScreeningsPagination.orderBy) {
        orderByFormatted.pop()
        userScreeningsPagination.orderBy.map((order) => {
          const orderKey = Object.keys(order)
          const orderValue = Object.values(order)
          orderByFormatted.push(`${orderKey[0]} ${orderValue[0]}`)
        })
      }

      const fetchedUserScreenings = await screening.fetchUserScreenings({
        ...{
          page: userScreeningsPagination.page,
          pageSize: userScreeningsPagination.pageSize,
          orderBy: orderByFormatted,
          ...(filtersValue.searchValue
            ? { userEmail: filtersValue.searchValue }
            : {}),
          ...(!filtersValue.screeningType ||
          filtersValue.screeningType ===
            ('All Screening Types' as FetchUserScreeningsScreeningTypeEnum)
            ? {}
            : {
                screeningType: filtersValue.screeningType,
              }),
          ...(!filtersValue.screeningStatus ||
          filtersValue.screeningStatus ===
            ('All Statuses' as FetchUserScreeningsStatusEnum)
            ? {}
            : {
                status: filtersValue.screeningStatus,
              }),
        },
      })

      setUserScreenings(fetchedUserScreenings.data)
      setUserScreeningsPagination(
        fetchedUserScreenings.pagination as PaginationResponse
      )
    },
  })

  useMountEffect(triggerFetchUserScreenings)

  const handlePaginationChange = (pagination: PaginationResponse) => {
    setUserScreeningsPagination({
      ...pagination,
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
      orderBy: pagination.orderBy,
    })

    triggerFetchUserScreenings()
  }

  const handleCloseModal = (updatedRecord: boolean) => {
    setShowModal(false)
    setRowActionDataUpdate(undefined)
    if (updatedRecord) {
      triggerFetchUserScreenings()
    }
  }

  return (
    <Card>
      <ConfirmUserScreeningStatusUpdateModal
        isOpen={showModal}
        onClose={handleCloseModal}
        userScreeningToUpdate={rowActionDataUpdate as RowActionDataUpdate}
      />
      <CardFormHeader
        header={
          <Header
            id="userScreening-header"
            headerName={t('UserScreeningTab.Card.Header', 'User screenings')}
            component="h2"
            variant={HeaderVariant.Card}
          />
        }
        headerContainerProps={{
          [theme.breakpoints.up('sm')]: {
            height: theme.spacing(10),
          },
        }}
        buttons={
          <UserScreeningTableSearchAndFilters
            searchValue={filtersValue.searchValue}
            handleFilterChange={({ filterField, filterValue }) => {
              setFiltersValue((prev) => ({
                ...prev,
                [filterField]: filterValue,
              }))
            }}
            triggerFetch={triggerFetchUserScreenings}
          />
        }
      />
      <Box>
        <UserScreeningTable
          userScreenings={userScreenings}
          handlePaginationChange={handlePaginationChange}
          pagination={userScreeningsPagination}
          handleRowAction={(status, userScreeningKey, screeningType) => {
            setRowActionDataUpdate({ status, userScreeningKey, screeningType })
            setShowModal(true)
          }}
        />
      </Box>
    </Card>
  )
}

export default UserScreeningTab
